import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { Spinner } from './ui';
import Layout from './Layout/Layout';
import useRoleAccess from '../hooks/useRoleAccess';
import { fetchUserPermissions } from '../store/authSlice';

// Content loader component for use within Layout
const ContentLoader = () => (
  <div
    className="flex w-full items-center justify-center"
    style={{ minHeight: 'calc(100vh - 200px)' }}
  >
    <Spinner size={40} />
  </div>
);

const TWO_MINUTES = 2 * 60 * 1000;

const ProtectedRoute = ({ children, requireOnboarding = true, requiredRoles = null, requiredDocTypes = null }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading, user } = useAppSelector((state) => state.auth);
  const { isCompleted: onboardingCompleted } = useAppSelector((state) => state.onboarding);
  const { hasAccess, hasRole, roles, userModulePermissions } = useRoleAccess();
  const [isInitializing, setIsInitializing] = useState(true);
  const lastPermsFetch = useRef(0);

  // Silently re-fetch permissions when the user returns to this tab, so a role change
  // made by an admin is visible without requiring a full page reload.
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastPermsFetch.current < TWO_MINUTES) return;
      lastPermsFetch.current = now;
      dispatch(fetchUserPermissions());
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isAuthenticated, dispatch]);

  // Check if user has company data (staff users have company data but may not have onboarding_completed flag)
  const hasCompanyData = user?.company || user?.company_name || user?.company_data;
  const companyStr = localStorage.getItem('company');
  const hasCompanyInStorage = !!companyStr;

  // User is considered to have completed onboarding if:
  // 1. onboarding_completed flag is set to true, OR
  // 2. User has company data (staff users work for a company, so they don't need onboarding)
  const shouldSkipOnboarding = onboardingCompleted || hasCompanyData || hasCompanyInStorage;

  // Wait for initial auth check to complete before making routing decisions
  useEffect(() => {
    // Check if we have a token in localStorage to determine if we should wait
    const token = localStorage.getItem('access_token');
    if (token) {
      // If we have a token, wait for auth state to initialize
      // The auth state should be set by getInitialState() immediately, but we wait
      // a tick to ensure Redux state is ready
      const timer = setTimeout(() => {
        setIsInitializing(false);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // No token, no need to wait
      setIsInitializing(false);
    }
  }, []);

  // Also stop initializing once auth state is determined (not loading)
  useEffect(() => {
    if (!isLoading) {
      setIsInitializing(false);
    }
  }, [isLoading]);

  // Check if children includes Layout component
  const hasLayout = React.Children.toArray(children).some(
    child => React.isValidElement(child) && child.type === Layout
  );

  // Show loading while initializing auth state
  if (isInitializing || isLoading) {
    // If Layout is present, render it with loader inside
    if (hasLayout) {
      return (
        <Layout>
          <ContentLoader />
        </Layout>
      );
    }
    // Otherwise show full-screen loader (for routes without Layout)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={40} />
      </div>
    );
  }

  // If not authenticated → redirect to login (preserving intended destination)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check if onboarding is required but not completed, and not already on onboarding page
  // Skip onboarding check as requested (handled elsewhere)
  /* 
  if (requireOnboarding && !shouldSkipOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // If onboarding is completed (or user has company data) and user tries to access onboarding page, redirect to dashboard
  if (shouldSkipOnboarding && location.pathname === '/onboarding') {
    return <Navigate to="/dashboard" replace />;
  }
  */

  // Check role-based access
  // If requiredRoles is specified, check if user has one of those roles
  if (requiredRoles && requiredRoles.length > 0) {
    if (!hasRole(requiredRoles)) {
      // User doesn't have required role - redirect to permission denied page
      return <Navigate to="/permission-denied" replace state={{ requiredRoles, roles }} />;
    }
  } else {
    // If role data not yet loaded, show loading spinner
    if (roles.length === 0 && isAuthenticated) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Spinner size={40} />
        </div>
      );
    }
    // Check if user has access to the current route path
    // Skip check for public routes like /onboarding
    const publicPaths = ['/onboarding', '/login'];
    if (!publicPaths.includes(location.pathname) && !hasAccess(location.pathname, requiredDocTypes)) {
      // User doesn't have access to this route - redirect to permission denied page
      return <Navigate to="/permission-denied" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;